<template>
    <page-container maxSize="2xl">
        <analytics-franchise-hub-page-header
            :backButtonHref="`/analytics/franchise-hub/${props.category}/${props.metric}/groups`"
            backButtonText="Back to List"
            :fetchParams="debouncedFetchParams"
            :category="props.category"
            :metric="props.metric"
            :pageTitle="props.groupName || props.groupId"
        />
        <div class="flex flex-col gap-6 xl:flex-row">
            <div class="shrink-0 xl:w-64">
                <analytics-sidebar
                    v-model="fetchParams"
                    :groupId="props.groupId"
                    hideCategoryFilter
                    hideResourceFilter
                    hideLockedSnippetsFilter
                    @reset="resetFilters()"
                    :minAllowedDate="minAllowedDate"
                ></analytics-sidebar>
            </div>

            <div class="w-full relative">
                <transition
                    enterFromClass="opacity-0 -translate-y-4"
                    enterToClass="opacity-100 translate-y-0"
                    leaveFromClass="opacity-100 translate-y-0"
                    leaveToClass="opacity-0 -translate-y-4"
                >
                    <div v-if="loading" class="absolute w-full transition pt-20">
                        <loading-bars></loading-bars>
                    </div>
                </transition>

                <vx-alert v-if="error && !loading" color="danger" :closeable="false">
                    {{ error }}
                </vx-alert>

                <div
                    v-if="debouncedFetchParams && !error"
                    class="flex flex-col gap-6"
                    :class="{
                        'opacity-0 pointer-events-none': loading,
                        'opacity-100 delay-200 transition duration-150': !loading,
                    }"
                >
                    <span v-if="!loading && totalCount > 0 && !error" class="text-sm">
                        There were <strong>{{ pluralized('resource', totalCount, true) }}</strong> sent by this group
                        matching the current filters.
                    </span>

                    <div v-if="visibleItems.length && !loading" class="grid gap-6 grid-cols-1 lg:grid-cols-12">
                        <vx-box
                            v-for="resource in visibleItems"
                            :key="`${resource.resource_type}_${resource.resource_id}`"
                            class="col-span-1 lg:col-span-6"
                        >
                            <resource-detail
                                :resource="resource"
                                :category="props.category"
                                :activeMetric="props.metric"
                            ></resource-detail>
                        </vx-box>
                    </div>

                    <empty-state v-else @reset="resetFilters" />

                    <div v-if="canLoadMore && !loading">
                        <vx-button
                            @click="loadMore()"
                            data-test="load-more-btn"
                            color="muted-dark"
                            size="xl"
                            class="grow-0"
                        >
                            Load More
                            <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
                        </vx-button>
                    </div>
                </div>
            </div>
        </div>
    </page-container>
</template>

<script setup>
import PageContainer from '~/components/general/PageContainer.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import { computed, ref, watch } from 'vue';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import AnalyticsSidebar from './AnalyticsSidebar.vue';
import { useDebouncedFetchParams } from '~/components/analytics/useDebouncedFetchParams';
import {
    getDateRange,
    getMinAllowedDate,
    resourceTypesOptions,
    metricsWithGoals,
    addCategoryToFetchParams,
} from '~/utils/analytics';
import { audienceTypes } from '~/constants/message';
import { SENDER_TYPES } from '~/constants/senderTypes';
import { analyticsCategoriesValues } from '~/constants/categories';
import LoadingBars from '~/components/general/LoadingBars.vue';
import { VxAlert, VxBox, VxButton } from '@voxie/frontend-components';
import ResourceDetail from './partial/resource-detail/ResourceDetail.vue';
import { useResourcesDetails } from '~/components/analytics/useResourcesDetails';
import { outboundMessagesAnalyticsClient } from '~/services/index';
import { useListAllPaginated } from '~/composables/useListAllPaginated';
import { useAnalyticsTargets } from '~/components/analytics/useAnalyticsTargets';
import axios from 'axios';
import { catchRequestError } from '~/utils/request';
import { useToasted } from '~/composables/useToasted';
import AnalyticsFranchiseHubPageHeader from '~/components/analytics/partial/AnalyticsFranchiseHubPageHeader.vue';
import { pluralized } from '~/components/filters';

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    category: {
        type: String,
        required: true,
        validator: (category) => analyticsCategoriesValues.includes(category),
    },
    metric: {
        type: String,
        required: true,
        validator: (metric) => metricsWithGoals.includes(metric),
    },
    groupId: {
        type: String,
        required: true,
    },
    groupName: {
        type: String,
        required: false,
    },
});

const defaultFilters = () => {
    const last30Days = getDateRange('last_30_days');

    return {
        range: [last30Days.start_date, last30Days.end_date],
        filters: {
            sender_types: SENDER_TYPES.map((senderType) => senderType.value),
            audience_types: audienceTypes().map((audienceType) => audienceType.name),
            resource_types: resourceTypesOptions.map((resourceType) => resourceType.value),
            resource_categories: undefined,
            resources: undefined,
        },
    };
};

const fetchAllParams = ref();
const perPage = 4;
const page = ref(1);

const { fetchParams, debouncedFetchParams, resetFilters } = useDebouncedFetchParams(defaultFilters());

const { error: targetsError } = useAnalyticsTargets();
const { fetchResourceDetails } = useResourcesDetails();
const { list: resources, error: resourcesError, fetchAll, loading } = useListAllPaginated(fetchAllParams);

const toasted = useToasted();

const minAllowedDate = getMinAllowedDate(true);

const totalCount = computed(() => resources.value.length);
const visibleItems = computed(() => (resources.value.length ? resources.value.slice(0, page.value * perPage) : []));
const canLoadMore = computed(() => visibleItems.value.length < totalCount.value && !loading.value);

const error = computed(() => resourcesError.value || targetsError.value);

watch(
    debouncedFetchParams,
    () => {
        fetchAllParams.value = JSON.parse(JSON.stringify(debouncedFetchParams.value));

        const fetchParams = addCategoryToFetchParams(fetchAllParams.value, props.category);

        // Fetch all resources
        fetchAll(() =>
            outboundMessagesAnalyticsClient.getExportResourceMetrics({ teamId, groupId: props.groupId }, fetchParams)
        );
    },
    {
        deep: true,
    }
);

watch(loading, (loading) => {
    if (!loading) {
        fetchDetails();
    }
});

const loadMore = () => {
    if (!canLoadMore.value) {
        return;
    }

    page.value++;

    fetchDetails();
};

const fetchDetails = () => {
    return fetchResourceDetails(teamId, visibleItems.value).catch((e) => {
        if (!axios.isCancel(e)) {
            toasted.error(catchRequestError(e));
        }
    });
};
</script>
